// "Volumen" (operator 03.11.2026): the open deals by the AI's estimate of their annual volume, weighed with the
// credits per form and packaging and the shares per certainty from the settings, ranked by expectation (credits ×
// chance); sortable and filterable by product type, form, packaging, finished goods or bulk, phase, chance and value
// (1–5 tablets, called sterne in the data). The briefing's tile and the deal's volume section use the same numbers.
import {
  basisOf,
  creditRegeln,
  credits,
  creditTeile,
  erwartung,
  fmtMenge,
  potenzial,
  sterne,
  VERPACKUNG_LABELS,
  VOLUMEN_BASIS_LABELS,
  VOLUMEN_FORM_LABELS,
  volumenRang,
  type Chance,
  type CreditRegeln,
  type Jahresvolumen,
  type VerpackungsArt,
  type VolumenEinheit,
  type VolumenForm,
} from "@pipeline/core";
import type { Deal } from "../api";
import { openDeals, S, stages } from "../state";
import { $, emptyState, esc } from "../util";

export interface VolumenZeile {
  deal: Deal;
  /** Weighed by certainty; the tablets come from these. */
  credits: number | null;
  /** Credits times the share of the chance; the place comes from these. */
  erwartung: number | null;
  potenzial: number | null;
  sterne: number | null;
  rang: number | null;
}

export const regeln = (): CreditRegeln => creditRegeln(S.meta?.rules?.volumenCredits);

/** Credits, expectation, value and place of every given deal; the place (by expectation) counts among the deals given. */
export function volumenZeilen(deals: readonly Deal[], r: CreditRegeln = regeln()): VolumenZeile[] {
  const e = new Map(deals.map((d) => [d, erwartung(d.jahresvolumen, r)]));
  const rang = volumenRang(deals, (d) => e.get(d)!);
  return deals.map((d) => {
    const c = credits(d.jahresvolumen, r);
    return { deal: d, credits: c, erwartung: e.get(d)!, potenzial: potenzial(d.jahresvolumen, r), sterne: sterne(c, r), rang: rang.get(d) ?? null };
  });
}

export const CHANCE_LABELS: Record<Chance, string> = { hoch: "hoch", mittel: "mittel", niedrig: "niedrig" };

/** The AI's chance of the deal as a small pill, with its reason on hover. */
export const chanceHtml = (jv: Jahresvolumen | null | undefined): string =>
  jv?.chance ? `<span class="chance c-${jv.chance}" title="${esc(jv.chanceBegruendung ?? "")}">Chance ${CHANCE_LABELS[jv.chance]}</span>` : `<span class="muted">–</span>`;

/** One round tablet with its score line, full or only outlined. */
const tablette = (voll: boolean) =>
  `<svg class="vtab${voll ? "" : " off"}" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><g transform="rotate(-35 8 8)"><circle cx="8" cy="8" r="6.4"/><line x1="8" y1="2.4" x2="8" y2="13.6"/></g></svg>`;

/** The value as 1 to 5 full tablets out of five (operator 03.11.2026), with the count for screen readers; a dash without an estimate. */
export const tablettenHtml = (n: number | null): string =>
  n === null
    ? `<span class="muted">–</span>`
    : `<span class="vtabs" role="img" aria-label="${n} von 5 Tabletten" title="${n} von 5 Tabletten">${[1, 2, 3, 4, 5].map((i) => tablette(i <= n)).join("")}</span>`;

/** "Pharma" (medicine or medical device), wherever a deal or an inquiry shows (operator 03.11.2026). */
export const pharmaBadge = (pharma: boolean): string => (pharma ? `<span class="pharma" title="Arzneimittel oder Medizinprodukt">Pharma</span>` : "");

/** Pharma badge and value in tablets for a card or a row; nothing for what is not known. */
export function markenHtml(x: { pharma: boolean; jahresvolumen: Jahresvolumen | null | undefined }, r: CreditRegeln = regeln()): string {
  const n = sterne(credits(x.jahresvolumen, r), r);
  const m = `${pharmaBadge(x.pharma)}${n === null ? "" : tablettenHtml(n)}`;
  return m ? `<span class="marks">${m}</span>` : "";
}

export const fmtCredits = (c: number) => Math.round(c).toLocaleString("de-DE");
const pct = (x: number) => `${Math.round(x * 100)} %`;

/** The open deals' estimates added up per form and unit, the most credits first: "34 Mio Kapseln". */
export function volumenSummen(deals: readonly Deal[], r: CreditRegeln = regeln()): { text: string; deals: number }[] {
  const sum = new Map<string, { form: VolumenForm; einheit: VolumenEinheit; menge: number; credits: number; deals: Set<string> }>();
  for (const d of deals)
    for (const p of (d.jahresvolumen?.posten ?? []).filter((x) => !x.beigestellt)) {
      const k = `${p.form}|${p.einheit}`;
      const s = sum.get(k) ?? { form: p.form, einheit: p.einheit, menge: 0, credits: 0, deals: new Set<string>() };
      s.menge += p.menge;
      s.credits += credits({ posten: [p] }, r) ?? 0;
      s.deals.add(d.id);
      sum.set(k, s);
    }
  return [...sum.values()].sort((a, b) => b.credits - a.credits).map((s) => ({ text: `${fmtMenge(s.menge, s.einheit)} ${VOLUMEN_FORM_LABELS[s.form]}`, deals: s.deals.size }));
}

/** The open deals' packaging units added up per kind, the most credits first: "4,1 Mio Blister" (03.11.2026). */
export function verpackungSummen(deals: readonly Deal[], r: CreditRegeln = regeln()): { text: string; deals: number }[] {
  const sum = new Map<VerpackungsArt, { menge: number; deals: Set<string> }>();
  for (const d of deals)
    for (const p of d.jahresvolumen?.verpackung ?? []) {
      const s = sum.get(p.art) ?? { menge: 0, deals: new Set<string>() };
      s.menge += p.menge;
      s.deals.add(d.id);
      sum.set(p.art, s);
    }
  return [...sum.entries()]
    .sort((a, b) => b[1].menge * r.verpackung[b[0]] - a[1].menge * r.verpackung[a[0]])
    .map(([art, s]) => ({ text: `${fmtMenge(s.menge, "Stück")} ${VERPACKUNG_LABELS[art]}`, deals: s.deals.size }));
}

/** One line per packaging kind: "10.000 Dosen". */
export const verpackungHtml = (d: { jahresvolumen: Jahresvolumen | null }): string =>
  (d.jahresvolumen?.verpackung ?? [])
    .map((p) => `<span class="vposten">${esc(`${fmtMenge(p.menge, "Stück")} ${VERPACKUNG_LABELS[p.art]}`)} <small class="muted">${VOLUMEN_BASIS_LABELS[basisOf(p.basis)]}</small></span>`)
    .join("");

/**
 * How the credits come about, as one reads it in the deal: "3,5 Mio Tabletten × 3 je 1.000 = 10.500 Credits · 5 Tabletten
 * ab 7.500" (operator 03.11.2026: the value has to be traceable).
 */
export function rechnungText(jv: Jahresvolumen | null | undefined, r: CreditRegeln = regeln()): string {
  const c = credits(jv, r);
  if (c === null) return "";
  const n = (x: number) => x.toLocaleString("de-DE", { maximumFractionDigits: 2 });
  const parts = creditTeile(jv, r).map((t) =>
    t.beigestellt
      ? `${t.text} beigestellt (zählt nur über die Verpackung)`
      : `${t.text} × ${n(t.faktor)} je ${t.je}${t.anteil < 1 ? ` × ${n(t.anteil * 100)} % (${VOLUMEN_BASIS_LABELS[t.basis]})` : ""}`,
  );
  const t = sterne(c, r)!;
  const tab = (k: number) => `${k} ${k === 1 ? "Tablette" : "Tabletten"}`;
  const range = t === 1 ? `${tab(1)} unter ${fmtCredits(r.sterne[0])}` : `${tab(t)} ab ${fmtCredits(r.sterne[t - 2]!)}`;
  return `${parts.join(" + ")} = ${fmtCredits(c)} Credits · ${range}${t < 5 ? `, ${tab(t + 1)} ab ${fmtCredits(r.sterne[t - 1]!)}` : ""}`;
}

/** One line per form: "30 Mio Kapseln", marked when estimated rather than stated. */
export const postenHtml = (d: Deal): string =>
  (d.jahresvolumen?.posten ?? [])
    .map((p) => `<span class="vposten">${esc(`${fmtMenge(p.menge, p.einheit)} ${VOLUMEN_FORM_LABELS[p.form]}`)} <small class="muted">${p.beigestellt ? "beigestellt" : VOLUMEN_BASIS_LABELS[basisOf(p.basis)]}</small></span>`)
    .join("");

const LIEFERFORM: Record<string, string> = { fertigware: "Fertigware", bulk: "Bulk", gemischt: "Fertigware + Bulk" };

export type VolumenSort = "erwartung" | "credits" | "deal" | "phase" | "produktart" | "lieferform" | "chance";
export interface VolumenFilter {
  produktart: string;
  form: string;
  /** A packaging kind the deal fills or packs. */
  verpackung: string;
  lieferform: string;
  phase: string;
  sterne: number;
  /** "ja": only Pharma, "nein": none. */
  pharma: "" | "ja" | "nein";
  chance: string;
  sort: VolumenSort;
  desc: boolean;
}

export const filter: VolumenFilter = { produktart: "", form: "", verpackung: "", lieferform: "", phase: "", sterne: 0, pharma: "", chance: "", sort: "erwartung", desc: true };

/** The rows the filter lets through, in its order; deals without an estimate always come last. */
export function gefiltert(rows: VolumenZeile[], f: VolumenFilter): VolumenZeile[] {
  const order = new Map(stages().map((s, i) => [s.id, i]));
  const chanceOrder: Record<string, number> = { hoch: 3, mittel: 2, niedrig: 1 };
  const key: Record<VolumenSort, (z: VolumenZeile) => string | number> = {
    erwartung: (z) => z.erwartung ?? 0,
    credits: (z) => z.credits ?? 0,
    chance: (z) => chanceOrder[z.deal.jahresvolumen?.chance ?? ""] ?? 0,
    deal: (z) => z.deal.ref,
    phase: (z) => order.get(z.deal.stage) ?? 99,
    produktart: (z) => z.deal.profil.produktarten.join(", ") || "~",
    lieferform: (z) => z.deal.profil.lieferform ?? "~",
  };
  const k = key[f.sort];
  return rows
    .filter(
      (z) =>
        (!f.produktart || z.deal.profil.produktarten.includes(f.produktart)) &&
        (!f.form || (z.deal.jahresvolumen?.posten ?? []).some((p) => p.form === f.form)) &&
        (!f.verpackung || (z.deal.jahresvolumen?.verpackung ?? []).some((p) => p.art === f.verpackung)) &&
        (!f.lieferform || z.deal.profil.lieferform === f.lieferform) &&
        (!f.phase || z.deal.stage === f.phase) &&
        (!f.sterne || (z.sterne ?? 0) >= f.sterne) &&
        (!f.pharma || z.deal.profil.pharma === (f.pharma === "ja")) &&
        (!f.chance || z.deal.jahresvolumen?.chance === f.chance),
    )
    .sort((a, b) => {
      if ((a.credits === null) !== (b.credits === null)) return a.credits === null ? 1 : -1;
      const x = k(a);
      const y = k(b);
      const c = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), "de");
      return (f.desc ? -c : c) || a.deal.ref.localeCompare(b.deal.ref);
    });
}

const select = (name: keyof VolumenFilter, label: string, options: [string, string][], value: string | number) =>
  `<label>${esc(label)}<select data-vol-filter="${name}"><option value="">alle</option>${options.map(([v, l]) => `<option value="${esc(v)}"${String(value) === v ? " selected" : ""}>${esc(l)}</option>`).join("")}</select></label>`;

export function volumenHtml(deals: readonly Deal[], f: VolumenFilter = filter, r: CreditRegeln = regeln()): string {
  const rows = volumenZeilen(deals, r);
  const known = rows.filter((z) => z.credits !== null).length;
  const shown = gefiltert(rows, f);
  const head = `<div class="brief-head"><span class="eyebrow">Pipeline</span><h1>Volumen</h1>
    <p>Was die offenen Deals im Jahr bringen würden, wie Claude es aus Mails, Anhängen und Angeboten schätzt. Jede Menge zählt nach ihrer Sicherheit: fest ${pct(r.sicherheit.fest)}, in Aussicht ${pct(r.sicherheit.in_aussicht)}, geschätzt ${pct(r.sicherheit.geschaetzt)}. Beigestellte Ware zählt nur über die Verpackung. Der Platz folgt der Erwartung, also Credits × Chance (hoch ${pct(r.chance.hoch)}, mittel ${pct(r.chance.mittel)}, niedrig ${pct(r.chance.niedrig)}). Alle Werte stellt ein Admin in den Einstellungen ein.</p>
    <p class="muted" style="font-size:12.5px">${known} von ${rows.length} offenen Deals mit Schätzung${known ? ` · ${volumenSummen(deals, r).map((s) => esc(s.text)).join(" · ")}` : ""}${verpackungSummen(deals, r).length ? `<br>Verpackung: ${verpackungSummen(deals, r).map((s) => esc(s.text)).join(" · ")}` : ""}</p></div>`;
  if (!rows.length) return head + emptyState("Keine offenen Deals", "");
  const present = <T extends string>(xs: T[]) => [...new Set(xs)].sort((a, b) => a.localeCompare(b, "de"));
  const forms = present(rows.flatMap((z) => (z.deal.jahresvolumen?.posten ?? []).map((p) => p.form)));
  const packs = present(rows.flatMap((z) => (z.deal.jahresvolumen?.verpackung ?? []).map((p) => p.art)));
  const phases = stages().filter((s) => rows.some((z) => z.deal.stage === s.id));
  const th = (key: VolumenSort, label: string) =>
    `<th aria-sort="${f.sort === key ? (f.desc ? "descending" : "ascending") : "none"}"><button type="button" class="link" data-vol-sort="${key}">${esc(label)}${f.sort === key ? (f.desc ? " ↓" : " ↑") : ""}</button></th>`;
  const filters = `<div class="vol-filter">
    ${select("pharma", "Pharma", [["ja", "nur Pharma"], ["nein", "ohne Pharma"]], f.pharma)}
    ${select("produktart", "Produktart", present(rows.flatMap((z) => z.deal.profil.produktarten)).map((p) => [p, p]), f.produktart)}
    ${select("form", "Form", forms.map((x) => [x, VOLUMEN_FORM_LABELS[x as VolumenForm]]), f.form)}
    ${select("verpackung", "Verpackung", packs.map((x) => [x, VERPACKUNG_LABELS[x as VerpackungsArt]]), f.verpackung)}
    ${select("lieferform", "Lieferform", Object.entries(LIEFERFORM), f.lieferform)}
    ${select("phase", "Phase", phases.map((s) => [s.id, s.label]), f.phase)}
    ${select("sterne", "Wertigkeit", [2, 3, 4, 5].map((n) => [String(n), `ab ${n} Tabletten`]), f.sterne || "")}
    ${select("chance", "Chance", [["hoch", "hoch"], ["mittel", "mittel"], ["niedrig", "niedrig"]], f.chance)}
  </div>`;
  const label = new Map(stages().map((s) => [s.id, s.label]));
  const body = shown.length
    ? `<div class="log-wrap"><table class="log vol"><thead><tr>${th("erwartung", "Platz")}${th("deal", "Deal")}${th("phase", "Phase")}<th>Jahresvolumen</th><th>Verpackung/Jahr</th>${th("produktart", "Produktart")}${th("lieferform", "Lieferform")}${th("chance", "Chance")}${th("credits", "Credits")}<th>Wertigkeit</th></tr></thead><tbody>${shown
        .map((z) => {
          const d = z.deal;
          const why = d.jahresvolumen?.begruendung ?? "";
          return `<tr${z.credits === null ? ` class="muted"` : ""}><td class="mono">${z.rang ?? "–"}</td>
            <td><a href="#deal-${esc(d.id)}"><span class="mono">${esc(d.ref)}</span> ${esc(d.name)}</a> ${pharmaBadge(d.profil.pharma)}<small class="muted vco">${esc(d.company)}</small></td>
            <td>${esc(label.get(d.stage) ?? d.stage)}</td>
            <td>${postenHtml(d) || `<span class="muted">keine Mengenangabe</span>`}${why ? `<small class="muted vwhy" title="${esc(why)}">${esc(why)}</small>` : ""}</td>
            <td>${verpackungHtml(d) || `<span class="muted">–</span>`}</td>
            <td>${esc(d.profil.produktarten.join(", ") || "–")}</td>
            <td>${esc(LIEFERFORM[d.profil.lieferform ?? ""] ?? "–")}</td>
            <td>${chanceHtml(d.jahresvolumen)}</td>
            <td class="mono"${z.potenzial !== null && z.credits !== null && z.potenzial > z.credits + 0.5 ? ` title="Potenzial ohne Abschlag: ${fmtCredits(z.potenzial)} Credits"` : ""}>${z.credits === null ? "–" : fmtCredits(z.credits)}</td>
            <td>${tablettenHtml(z.sterne)}</td></tr>`;
        })
        .join("")}</tbody></table></div>`
    : emptyState("Kein Deal passt zum Filter", "Einen Filter auf „alle“ stellen.");
  return head + filters + body;
}

export function renderVolumen(): void {
  if (!S.loaded) {
    $("#v-volumen").innerHTML = emptyState("Lade Volumen …", "");
    return;
  }
  $("#v-volumen").innerHTML = volumenHtml(openDeals());
}

export function bindVolumen(): void {
  const el = $("#v-volumen");
  el.addEventListener("change", (e) => {
    const s = (e.target as HTMLElement).closest<HTMLSelectElement>("[data-vol-filter]");
    if (!s) return;
    const k = s.dataset.volFilter as keyof VolumenFilter;
    if (k === "sterne") filter.sterne = Number(s.value) || 0;
    else if (k === "pharma") filter.pharma = s.value as VolumenFilter["pharma"];
    else if (k === "chance") filter.chance = s.value;
    else if (k === "produktart" || k === "form" || k === "verpackung" || k === "lieferform" || k === "phase") filter[k] = s.value;
    renderVolumen();
  });
  el.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-vol-sort]");
    if (!b) return;
    const k = b.dataset.volSort as VolumenSort;
    filter.desc = filter.sort === k ? !filter.desc : k === "credits" || k === "erwartung" || k === "chance";
    filter.sort = k;
    renderVolumen();
  });
}
